import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useDeliveryAddress } from '../context/DeliveryAddressContext';
import { API_URL } from '../config/api';
import OrderMessages from './OrderMessages';
import ReviewModal from './ReviewModal';
import OrderReceiptModal from './OrderReceiptModal';
import './ActiveOrder.css';

const ActiveOrder = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { currentAddress } = useDeliveryAddress();
  const navigate = useNavigate();
  const [activeOrder, setActiveOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [estimatedTime, setEstimatedTime] = useState(null);
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
  const [showMessages, setShowMessages] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [canReview, setCanReview] = useState(false);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const courierMarkerRef = useRef(null);
  const directionsServiceRef = useRef(null);
  const lastOrderIdRef = useRef(null);

  // Check if Google Maps is loaded
  useEffect(() => {
    const checkGoogleMaps = () => {
      if (window.google && window.google.maps) {
        setGoogleMapsLoaded(true);
      } else {
        // Retry after a short delay
        setTimeout(checkGoogleMaps, 100);
      }
    };
    checkGoogleMaps();
  }, []);

  useEffect(() => {
    if (user) {
      fetchActiveOrder();
      // Poll for status and driver location updates every 3 seconds for a live feel
      const interval = setInterval(fetchActiveOrder, 3000);
      return () => clearInterval(interval);
    } else {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (activeOrder && activeOrder.status === 'delivered') {
      checkCanReview();
    } else {
      setCanReview(false);
    }
  }, [activeOrder]);

  const checkCanReview = async () => {
    try {
      const response = await axios.get(`${API_URL}/reviews/order/${activeOrder._id}/can-review`);
      setCanReview(response.data.canReview);
    } catch (error) {
      console.error('Error checking review status:', error);
      setCanReview(false);
    }
  };

  useEffect(() => {
    if (activeOrder && googleMapsLoaded && window.google && window.google.maps) {
      const orderId = activeOrder._id;
      const courierLat = activeOrder.courierLocation?.coordinates?.lat;
      const courierLng = activeOrder.courierLocation?.coordinates?.lng;

      // If same order and only courier location changed, move the driver marker and refresh ETA when out for delivery
      if (
        lastOrderIdRef.current === orderId &&
        mapInstanceRef.current &&
        courierMarkerRef.current &&
        courierLat != null &&
        courierLng != null
      ) {
        courierMarkerRef.current.setPosition({ lat: courierLat, lng: courierLng });
        if (activeOrder.status === 'outForDelivery') {
          calculateDrivingTime();
        }
        return;
      }
      lastOrderIdRef.current = orderId;

      const timer = setTimeout(() => {
        initializeMap();
        calculateDrivingTime();
      }, 100);
      return () => {
        clearTimeout(timer);
        markersRef.current.forEach(marker => marker.setMap(null));
        markersRef.current = [];
        courierMarkerRef.current = null;
      };
    }
  }, [activeOrder, currentAddress, googleMapsLoaded]);

  const fetchActiveOrder = async () => {
    try {
      const response = await axios.get(`${API_URL}/orders/active`);
      setActiveOrder(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        setActiveOrder(null);
      } else {
        console.error('Error fetching active order:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const initializeMap = () => {
    if (!mapRef.current || !activeOrder || !window.google || !window.google.maps) {
      console.warn('Cannot initialize map - missing requirements:', {
        mapRef: !!mapRef.current,
        activeOrder: !!activeOrder,
        google: !!window.google,
        maps: !!(window.google && window.google.maps)
      });
      return;
    }

    // Clear existing markers
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];

    const restaurant = activeOrder.restaurant;
    if (!restaurant?.address?.coordinates) {
      console.warn('Restaurant address coordinates missing:', restaurant);
      return;
    }

    const restaurantLat = restaurant.address.coordinates.lat;
    const restaurantLng = restaurant.address.coordinates.lng;

    // Center map on restaurant
    const center = { lat: restaurantLat, lng: restaurantLng };

    // Initialize map
    if (!mapInstanceRef.current) {
      mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
        zoom: 13,
        center: center,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false
      });
    } else {
      mapInstanceRef.current.setCenter(center);
    }

    // Add restaurant marker
    const restaurantMarker = new window.google.maps.Marker({
      position: { lat: restaurantLat, lng: restaurantLng },
      map: mapInstanceRef.current,
      title: restaurant.name || activeOrder.restaurantName,
      icon: {
        url: 'http://maps.google.com/mapfiles/ms/icons/red-dot.png'
      }
    });
    markersRef.current.push(restaurantMarker);

    // Add courier marker if available (driver's live location)
    if (activeOrder.courierLocation?.coordinates?.lat && activeOrder.courierLocation?.coordinates?.lng) {
      const courierMarker = new window.google.maps.Marker({
        position: {
          lat: activeOrder.courierLocation.coordinates.lat,
          lng: activeOrder.courierLocation.coordinates.lng
        },
        map: mapInstanceRef.current,
        title: 'Courier',
        icon: {
          url: 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png'
        }
      });
      markersRef.current.push(courierMarker);
      courierMarkerRef.current = courierMarker;

      // Add delivery address marker if available
      if (activeOrder.deliveryAddress?.coordinates?.lat && activeOrder.deliveryAddress?.coordinates?.lng) {
        const deliveryMarker = new window.google.maps.Marker({
          position: {
            lat: activeOrder.deliveryAddress.coordinates.lat,
            lng: activeOrder.deliveryAddress.coordinates.lng
          },
          map: mapInstanceRef.current,
          title: 'Delivery Address',
          icon: {
            url: 'http://maps.google.com/mapfiles/ms/icons/green-dot.png'
          }
        });
        markersRef.current.push(deliveryMarker);

        // Fit bounds to show all markers
        const bounds = new window.google.maps.LatLngBounds();
        bounds.extend({ lat: restaurantLat, lng: restaurantLng });
        bounds.extend({
          lat: activeOrder.courierLocation.coordinates.lat,
          lng: activeOrder.courierLocation.coordinates.lng
        });
        bounds.extend({
          lat: activeOrder.deliveryAddress.coordinates.lat,
          lng: activeOrder.deliveryAddress.coordinates.lng
        });
        mapInstanceRef.current.fitBounds(bounds);
      }
    } else {
      courierMarkerRef.current = null;
      // If no courier location, just show restaurant and delivery address
      if (activeOrder.deliveryAddress?.coordinates?.lat && activeOrder.deliveryAddress?.coordinates?.lng) {
        const deliveryMarker = new window.google.maps.Marker({
          position: {
            lat: activeOrder.deliveryAddress.coordinates.lat,
            lng: activeOrder.deliveryAddress.coordinates.lng
          },
          map: mapInstanceRef.current,
          title: 'Delivery Address',
          icon: {
            url: 'http://maps.google.com/mapfiles/ms/icons/green-dot.png'
          }
        });
        markersRef.current.push(deliveryMarker);

        const bounds = new window.google.maps.LatLngBounds();
        bounds.extend({ lat: restaurantLat, lng: restaurantLng });
        bounds.extend({
          lat: activeOrder.deliveryAddress.coordinates.lat,
          lng: activeOrder.deliveryAddress.coordinates.lng
        });
        mapInstanceRef.current.fitBounds(bounds);
      }
    }
  };

  const getStatusText = (status) => {
    const statusMap = {
      'pending': t('activeOrder.status.pending') || 'Pending',
      'confirmed': t('activeOrder.status.confirmed') || 'Confirmed',
      'preparing': t('activeOrder.status.preparing') || 'Preparing',
      'outForDelivery': t('activeOrder.status.outForDelivery') || 'Out for Delivery',
      'delivered': t('activeOrder.status.delivered') || 'Delivered',
      'cancelled': t('activeOrder.status.cancelled') || 'Cancelled'
    };
    return statusMap[status] || status;
  };

  const getDeliveryMethodText = (method) => {
    const methodMap = {
      'delivery': t('activeOrder.delivery') || 'Delivery',
      'pickup': t('activeOrder.pickup') || 'Pickup',
      'eatIn': t('activeOrder.eatIn') || 'Eat In'
    };
    return methodMap[method] || method;
  };

  const getPaymentMethodText = (method) => {
    const methodMap = {
      'stripe': t('activeOrder.cardPayment') || 'Card Payment',
      'cash': t('activeOrder.cash') || 'Cash on Delivery',
      'other': t('activeOrder.other') || 'Other'
    };
    return methodMap[method] || method;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Parse restaurant/store deliveryTime string (e.g. "30-45 min", "25 min") to minutes for estimate. Returns 0 if days or unparseable.
  const parseDeliveryTimeToMinutes = (deliveryTimeStr) => {
    if (!deliveryTimeStr || typeof deliveryTimeStr !== 'string') return 0;
    const s = deliveryTimeStr.trim();
    // "X-Y min" or "X-Y minuten" -> use upper bound (Y)
    const rangeMatch = s.match(/^(\d+)\s*-\s*(\d+)\s*(?:min|minute|minuten|minuuttia)?/i);
    if (rangeMatch) {
      const a = parseInt(rangeMatch[1], 10);
      const b = parseInt(rangeMatch[2], 10);
      if (!isNaN(a) && !isNaN(b) && b <= 180) return Math.max(a, b); // cap 180 = 3h, avoid parsing "2-3 days" as 3
      if (!isNaN(a) && !isNaN(b)) return Math.round((a + b) / 2);
    }
    // "X min" or just "X"
    const singleMatch = s.match(/^(\d+)\s*(?:min|minute|minuten|minuuttia)?/i);
    if (singleMatch) {
      const n = parseInt(singleMatch[1], 10);
      if (!isNaN(n) && n <= 300) return n;
    }
    // "Same day" or day-based: don't add to live estimate
    if (/\d+\s*days?|day|päivä/i.test(s)) return 0;
    return 0;
  };

  const formatMinutesAsEstimate = (totalMinutes) => {
    if (totalMinutes < 60) {
      return `${totalMinutes} ${t('activeOrder.minutes') || 'minutes'}`;
    }
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (minutes === 0) return `${hours}h`;
    return `${hours}h ${minutes}m`;
  };

  const calculateDrivingTime = () => {
    if (!activeOrder || !window.google || !window.google.maps) {
      console.warn('Cannot calculate driving time - Google Maps not loaded');
      return;
    }

    const restaurant = activeOrder.restaurant;
    const userLocation = activeOrder.deliveryAddress?.coordinates || currentAddress?.coordinates;
    if (!userLocation || !userLocation.lat || !userLocation.lng) {
      console.warn('Cannot calculate driving time - user location missing');
      setEstimatedTime(null);
      return;
    }

    if (!directionsServiceRef.current) {
      directionsServiceRef.current = new window.google.maps.DirectionsService();
    }

    const courierLat = activeOrder.courierLocation?.coordinates?.lat;
    const courierLng = activeOrder.courierLocation?.coordinates?.lng;
    const hasDriverLocation = courierLat != null && courierLng != null && !isNaN(parseFloat(courierLat)) && !isNaN(parseFloat(courierLng));
    const customerDest = new window.google.maps.LatLng(userLocation.lat, userLocation.lng);

    // Out for delivery: driver is on the way to customer — only use driver → customer ETA (avoids inflated/increasing ETA)
    if (activeOrder.status === 'outForDelivery' && hasDriverLocation) {
      const driverOrigin = new window.google.maps.LatLng(parseFloat(courierLat), parseFloat(courierLng));
      directionsServiceRef.current.route(
        {
          origin: driverOrigin,
          destination: customerDest,
          travelMode: window.google.maps.TravelMode.DRIVING
        },
        (result, status) => {
          if (status === window.google.maps.DirectionsStatus.OK && result?.routes?.[0]?.legs?.[0]) {
            const minutes = Math.ceil(result.routes[0].legs[0].duration.value / 60);
            setEstimatedTime(formatMinutesAsEstimate(Math.max(1, minutes)));
          } else {
            setEstimatedTime(t('activeOrder.calculating') || 'Calculating...');
          }
        }
      );
      return;
    }

    if (!restaurant?.address?.coordinates) {
      setEstimatedTime(null);
      return;
    }

    const prepMinutes = parseDeliveryTimeToMinutes(restaurant.deliveryTime);
    const restLat = restaurant.address.coordinates.lat;
    const restLng = restaurant.address.coordinates.lng;
    const restOrigin = new window.google.maps.LatLng(restLat, restLng);

    const setTotalEstimate = (driverToRestMinutes, restaurantToCustomerMinutes) => {
      const total = prepMinutes + driverToRestMinutes + restaurantToCustomerMinutes;
      setEstimatedTime(formatMinutesAsEstimate(total));
    };

    // Restaurant -> customer (for preparing / no driver location)
    directionsServiceRef.current.route(
      {
        origin: restOrigin,
        destination: customerDest,
        travelMode: window.google.maps.TravelMode.DRIVING
      },
      (resultRestToCustomer, statusRestToCustomer) => {
        if (statusRestToCustomer !== window.google.maps.DirectionsStatus.OK || !resultRestToCustomer.routes?.[0]?.legs?.[0]) {
          setEstimatedTime(t('activeOrder.calculating') || 'Calculating...');
          return;
        }
        const restToCustomerMinutes = Math.ceil(resultRestToCustomer.routes[0].legs[0].duration.value / 60);

        if (!hasDriverLocation) {
          setTotalEstimate(0, restToCustomerMinutes);
          return;
        }

        // Driver -> restaurant (pickup), then total = prep + driverToRest + restToCustomer
        const driverOrigin = new window.google.maps.LatLng(parseFloat(courierLat), parseFloat(courierLng));
        directionsServiceRef.current.route(
          {
            origin: driverOrigin,
            destination: restOrigin,
            travelMode: window.google.maps.TravelMode.DRIVING
          },
          (resultDriverToRest, statusDriverToRest) => {
            const driverToRestMinutes = (statusDriverToRest === window.google.maps.DirectionsStatus.OK && resultDriverToRest.routes?.[0]?.legs?.[0])
              ? Math.ceil(resultDriverToRest.routes[0].legs[0].duration.value / 60)
              : 0;
            setTotalEstimate(driverToRestMinutes, restToCustomerMinutes);
          }
        );
      }
    );
  };

  const getEstimatedDeliveryTime = () => {
    if (estimatedTime) {
      return estimatedTime;
    }
    return t('activeOrder.calculating') || 'Calculating...';
  };

  if (loading) {
    return null;
  }

  if (!activeOrder) {
    return null;
  }

  const restaurant = activeOrder.restaurant || {};

  const isDelivered = activeOrder.status === 'delivered';

  return (
    <div className="active-order-container">
      <div className="active-order">
        <div className="active-order-header">
          <h2>{t('activeOrder.title') || 'Active Order'}</h2>
          <span className={`status-badge status-${activeOrder.status}`}>
            {getStatusText(activeOrder.status)}
          </span>
        </div>

        {/* Delivered success state */}
        {isDelivered && (
          <div className="active-order-delivered-banner">
            <i className="fas fa-check-circle active-order-delivered-icon"></i>
            <p className="active-order-delivered-title">{t('activeOrder.orderDelivered') || 'Order delivered'}</p>
            <p className="active-order-delivered-text">{t('activeOrder.orderDeliveredText') || 'Your order has been delivered. Thank you for your order!'}</p>
          </div>
        )}

        {/* Google Maps - hide when delivered */}
        {!isDelivered && restaurant?.address?.coordinates && googleMapsLoaded && (
          <div className="active-order-map">
            <div ref={mapRef} className="map-container"></div>
          </div>
        )}

        {/* Estimated Delivery Time and Courier - hide when delivered */}
        {!isDelivered && activeOrder.deliveryMethod === 'delivery' && (
          <div className="estimated-delivery-subtotal">
            <div className="estimated-delivery-small">
              <i className="fas fa-clock"></i>
              <span>
                {t('activeOrder.estimatedDelivery') || 'Estimated delivery'}: {getEstimatedDeliveryTime()}
              </span>
            </div>
            {activeOrder.courier?.firstName && (
              <div className="courier-info-small">
                <i className="fas fa-user"></i>
                <span>
                  {activeOrder.courier.firstName} {activeOrder.courier.lastName ? activeOrder.courier.lastName.charAt(0) + '.' : ''}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Order Items */}
        <div className="active-order-items">
          <h3>{t('activeOrder.items') || 'Order Items'}</h3>
          {activeOrder.items.map((item, index) => (
            <div key={index} className="active-order-item">
              <div className="active-order-item-info">
                <div>
                  <span className="item-quantity">{item.quantity}x</span>
                  <span className="item-name">{item.name}</span>
                </div>
                {(item.variationOptionPrices && item.variationOptionPrices.length > 0) ? (
                  <div className="active-order-variations">
                    {item.variationOptionPrices.map((v, vIdx) => (
                      <div key={vIdx} className="active-order-variation-group">
                        <span className="active-order-variation-name">{v.variationName}:</span>
                        <div className="active-order-variation-options">
                          <span className="active-order-variation-badge">
                            {v.optionName}{v.price != null && v.price !== 0 ? ` (+€${Number(v.price).toFixed(2)})` : ''}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : item.selectedVariations && (
                  <div className="active-order-variations">
                    {Array.isArray(item.selectedVariations) ? (
                      item.selectedVariations.map((variation, vIdx) => (
                        <div key={vIdx} className="active-order-variation-group">
                          <span className="active-order-variation-name">{variation.name}:</span>
                          <div className="active-order-variation-options">
                            {variation.selectedOptions?.map((option, oIdx) => (
                              <span key={oIdx} className="active-order-variation-badge">{option}</span>
                            ))}
                          </div>
                        </div>
                      ))
                    ) : (
                      Object.entries(item.selectedVariations).map(([variationName, selectedOptions], vIdx) => (
                        <div key={vIdx} className="active-order-variation-group">
                          <span className="active-order-variation-name">{variationName}:</span>
                          <div className="active-order-variation-options">
                            {Array.isArray(selectedOptions) ? (
                              selectedOptions.map((option, oIdx) => (
                                <span key={oIdx} className="active-order-variation-badge">{option}</span>
                              ))
                            ) : (
                              <span className="active-order-variation-badge">{selectedOptions}</span>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
                {item.selectedIngredients && item.selectedIngredients.length > 0 && (
                  <div className="active-order-ingredients">
                    <span className="active-order-ingredients-label">{t('cart.ingredientsIncluded') || t('activeOrder.ingredients') || 'Ingredients'}:</span>
                    <span>{item.selectedIngredients.join(', ')}</span>
                  </div>
                )}
              </div>
              <span className="item-price">€{(item.price * item.quantity).toFixed(2)}</span>
            </div>
          ))}
        </div>

        {/* Order Details and Restaurant Information - Side by Side */}
        <div className="active-order-info-section">
          <div className="info-boxes-container">
            {/* Left Box: Order Details */}
            <div className="active-order-details info-box-left">
              <h3>{t('activeOrder.orderDetails') || 'Order Details'}</h3>
              <div className="detail-row">
                <span><strong>{t('activeOrder.orderNumber') || 'Order Number'}:</strong></span>
                <span>{activeOrder.orderNumber}</span>
              </div>
              <div className="detail-row">
                <span><strong>{t('activeOrder.orderDate') || 'Order Date'}:</strong></span>
                <span>{formatDate(activeOrder.createdAt)}</span>
              </div>
              <div className="detail-row">
                <span><strong>{t('activeOrder.serviceProvider') || 'Service Provider'}:</strong></span>
                <span>Pussikuskit</span>
              </div>
            </div>

            {/* Right Box: Restaurant Information */}
            <div className="active-order-restaurant info-box-right">
              <h3>{t('activeOrder.restaurantInfo') || 'Restaurant Information'}</h3>
              <div className="restaurant-info-row">
                <span><strong>{t('activeOrder.name') || 'Name'}:</strong></span>
                <span>{activeOrder.restaurantName}</span>
              </div>
              {restaurant?.address && (
                <div className="restaurant-info-row">
                  <span><strong>{t('activeOrder.address') || 'Address'}:</strong></span>
                  <span>
                    {restaurant.address.street}, {restaurant.address.city} {restaurant.address.zipCode}
                  </span>
                </div>
              )}
              {restaurant.phoneNumber && (
                <div className="restaurant-info-row">
                  <span><strong>{t('activeOrder.phone') || 'Phone'}:</strong></span>
                  <span>{restaurant.phoneNumber}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="active-order-action-buttons">
          <button
            type="button"
            className="btn btn-secondary contact-support-btn-small"
            onClick={() => setShowReceipt(true)}
          >
            <i className="fas fa-receipt"></i>
            {t('activeOrder.viewReceipt') || 'View receipt'}
          </button>
          <button
            type="button"
            className="btn btn-primary contact-support-btn-small"
            onClick={() => setShowMessages(true)}
          >
            <i className="fas fa-headset"></i>
            {t('activeOrder.contactSupport') || 'Contact Support'}
          </button>
          {isDelivered && canReview && (
            <button
              type="button"
              className="btn btn-primary contact-support-btn-small active-order-review-btn"
              onClick={() => setShowReviewModal(true)}
            >
              <i className="fas fa-star"></i>
              {t('activeOrder.reviewOrder')}
            </button>
          )}
        </div>
      </div>

      {showReceipt && (
        <OrderReceiptModal order={activeOrder} onClose={() => setShowReceipt(false)} />
      )}

      {showMessages && (
        <OrderMessages order={activeOrder} onClose={() => setShowMessages(false)} />
      )}

      {showReviewModal && (
        <ReviewModal
          order={activeOrder}
          onClose={() => {
            setShowReviewModal(false);
            checkCanReview();
          }}
          onReviewSubmitted={() => {
            setCanReview(false);
          }}
        />
      )}
    </div>
  );
};

export default ActiveOrder;
